/**
 * How long a repeat of the same link counts as the same click. A guest who taps again because
 * nothing looked like it happened has reported one intent, not two — but coming back to the same
 * link later is a second visit, and that is worth counting.
 */
const LINK_CLICK_WINDOW_MS = 1000;

/**
 * Module scope, not a ref: the same link is rendered by more than one component (a card body and
 * its CTA, the same offer in a carousel and in the message above it), and a per-instance memory
 * would let each of them report the same burst.
 */
const lastLinkClickAt = new Map<string, number>();

/**
 * A wall clock can step backwards — an NTP correction, a user changing the time — and a timestamp
 * that is suddenly in the future must not swallow clicks until it catches up. Anything not inside
 * the window *behind* us is forgotten.
 */
function isWithinWindow({ at, now }: { at: number; now: number }): boolean {
  const elapsed = now - at;
  return elapsed >= 0 && elapsed <= LINK_CLICK_WINDOW_MS;
}

/**
 * Shared by the chat surface and the MCP panel so one funnel can compare them: two surfaces that
 * counted repeats differently would differ by their own measurement, not by guest behaviour.
 */
export function isRepeatOfRecentClick({ key, now }: { key: string; now: number }): boolean {
  for (const [seen, at] of lastLinkClickAt) {
    if (!isWithinWindow({ at, now })) lastLinkClickAt.delete(seen);
  }
  const previous = lastLinkClickAt.get(key);
  lastLinkClickAt.set(key, now);
  return previous !== undefined && isWithinWindow({ at: previous, now });
}
